import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminOrderDetail.css';

function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', payment_status: '', notes: '' });
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [shipmentForm, setShipmentForm] = useState({
    tracking_number: '',
    carrier: '',
    shipping_method: '',
    notes: '',
  });
  const [showRMAStatusModal, setShowRMAStatusModal] = useState(false);
  const [editingRMA, setEditingRMA] = useState(null);
  const [rmaStatusForm, setRmaStatusForm] = useState({ status: '', notes: '', tracking_number: '' });
  const [showRefundStatusModal, setShowRefundStatusModal] = useState(false);
  const [editingRefund, setEditingRefund] = useState(null);
  const [refundStatusForm, setRefundStatusForm] = useState({ status: '', transaction_id: '', notes: '' });
  const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
  const [paymentStatusForm, setPaymentStatusForm] = useState({ 
    payment_status: '', 
    amount: '', 
    transaction_id: '', 
    payment_method: '', 
    notes: '' 
  });

  const { data: order, isLoading } = useQuery({
    queryKey: ['admin-order', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/orders/${id}`);
      return res.data;
    },
  });

  // Pristatymo metodai (vežėjų sąrašas imamas iš jų lauko „Vežėjas“)
  const { data: shippingMethodsData } = useQuery({
    queryKey: ['admin-shipping-methods'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/shipping-methods?limit=100');
      return res.data;
    },
  });
  const shippingMethods = shippingMethodsData?.methods ?? [];

  const updateStatusMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.put(`/admin/orders/${id}/status`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      queryClient.invalidateQueries(['admin-orders']);
      setShowStatusModal(false);
      setStatusForm({ status: '', payment_status: '', notes: '' });
      notification.success('Užsakymo statusas atnaujintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant statusą');
    },
  });

  const { data: paymentHistory } = useQuery({
    queryKey: ['payment-status-history', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/payment-status/order/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  // Gauti sąskaitas faktūras pagal užsakymo ID
  const { data: invoicesData } = useQuery({
    queryKey: ['admin-invoices', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/invoices?order_id=${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  // Gauti pick & pack duomenis
  const { data: pickPackData } = useQuery({
    queryKey: ['pick-pack', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/pick-pack/order/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  // Sukurti pick list mutacija
  const createPickListMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post('/admin/pick-pack', { order_id: parseInt(id) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pick-pack', id]);
      queryClient.invalidateQueries(['admin-order', id]);
      notification.success('Surinkimo lapas sukurtas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida kuriant surinkimo lapą');
    },
  });

  const updatePaymentStatusMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.put(`/admin/payment-status/order/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      queryClient.invalidateQueries(['payment-status-history', id]);
      setShowPaymentStatusModal(false);
      setPaymentStatusForm({ 
        payment_status: '', 
        amount: '', 
        transaction_id: '', 
        payment_method: '', 
        notes: '' 
      });
      notification.success('Mokėjimo būsena atnaujinta');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant mokėjimo būseną');
    },
  });

  const handleStatusUpdate = () => {
    if (!statusForm.status && !statusForm.payment_status) {
      notification.warning('Pasirinkite bent vieną statusą');
      return;
    }
    updateStatusMutation.mutate(statusForm);
  };

  const getStatusOptions = (currentStatus) => {
    const transitions = {
      'pending': ['processing', 'cancelled'],
      'processing': ['shipped', 'cancelled'],
      'shipped': ['delivered', 'cancelled'],
      'delivered': [],
      'cancelled': [],
    };
    return transitions[currentStatus] || [];
  };

  const getPaymentStatusOptions = (currentStatus) => {
    const transitions = {
      'pending': ['paid', 'failed'],
      'paid': ['refunded'],
      'failed': ['pending'],
      'refunded': [],
    };
    return transitions[currentStatus] || [];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const getInvoiceStatusBadgeClass = (status) => {
    const classes = {
      'draft': 'status-draft',
      'sent': 'status-sent',
      'paid': 'status-paid',
      'cancelled': 'status-cancelled',
    };
    return classes[status] || 'status-default';
  };

  const getInvoiceStatusLabel = (status) => {
    const labels = {
      'draft': 'Juodraštis',
      'sent': 'Išsiųsta',
      'paid': 'Apmokėta',
      'cancelled': 'Atšaukta',
    };
    return labels[status] || status;
  };

  const handlePrint = async (type, format = 'html') => {
    try {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      const url = `/api/admin/orders/pdf/${type}/${id}?format=${format}`;
      
      const headers = {
        'Authorization': `Bearer ${token}`,
      };
      
      if (sessionId) {
        headers['x-session-id'] = sessionId;
      }
      
      if (format === 'pdf') {
        // Atsisiųsti PDF failą su autentifikacijos tokenu
        const response = await fetch(url, { headers });
        
        if (!response.ok) {
          // Bandyti gauti JSON klaidą, jei ne - naudoti tekstą
          let errorMessage = 'Klaida gaunant PDF';
          try {
            const error = await response.json();
            errorMessage = error.error || error.details || errorMessage;
            if (error.suggestion) {
              errorMessage += `\n${error.suggestion}`;
            }
          } catch (e) {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        // Patikrinti ar response tikrai yra PDF
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/pdf')) {
          // Jei ne PDF, bandyti gauti klaidos pranešimą
          const text = await response.text();
          try {
            const error = JSON.parse(text);
            throw new Error(error.error || error.details || 'Gautas neteisingas formatas');
          } catch (e) {
            throw new Error('Gautas neteisingas formatas. Tikėtasi PDF, gauta: ' + contentType);
          }
        }
        
        const blob = await response.blob();
        
        // Patikrinti ar blob nėra tuščias
        if (blob.size === 0) {
          throw new Error('Gautas tuščias PDF failas');
        }
        
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${type}-${id}.pdf`;
        link.type = 'application/pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        // Atidaryti HTML naujame lange su autentifikacijos tokenu
        const response = await fetch(url, { headers });
        
        if (!response.ok) {
          // Bandyti gauti JSON klaidą, jei ne - naudoti tekstą
          let errorMessage = 'Klaida gaunant HTML';
          try {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
          } catch (e) {
            errorMessage = await response.text() || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        let html = await response.text();
        // Pašalinti portą iš visų URL, kad nuotraukos krautųsi per 443 (ne per :3009)
        html = html.replace(/:3009/g, '').replace(/:3000/g, '').replace(/:8080/g, '');
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
        }
      }
    } catch (error) {
      notification.error(error.message || 'Klaida atidarant dokumentą');
    }
  };

  // Siuntų mutacijos
  const createShipmentMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/shipments', { ...data, order_id: parseInt(id) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      setShowShipmentModal(false);
      setShipmentForm({
        tracking_number: '',
        carrier: '',
        shipping_method: '',
        notes: '',
      });
      notification.success('Siunta sukurta. Užsakymo statusas automatiškai pakeistas į "Išsiųstas"');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida kuriant siuntą');
    },
  });

  const updateShipmentMutation = useMutation({
    mutationFn: async ({ shipmentId, data }) => {
      return apiClient.put(`/admin/shipments/${shipmentId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      setShowShipmentModal(false);
      setEditingShipment(null);
      setShipmentForm({
        tracking_number: '',
        carrier: '',
        shipping_method: '',
        notes: '',
      });
      notification.success('Siunta atnaujinta');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant siuntą');
    },
  });

  const deleteShipmentMutation = useMutation({
    mutationFn: async (shipmentId) => {
      return apiClient.delete(`/admin/shipments/${shipmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      notification.success('Siunta pašalinta');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida šalinant siuntą');
    },
  });

  const handleOpenShipmentModal = (shipment = null) => {
    if (shipment) {
      setEditingShipment(shipment);
      setShipmentForm({
        tracking_number: shipment.tracking_number,
        carrier: shipment.carrier || '',
        shipping_method: shipment.shipping_method || '',
        notes: shipment.notes || '',
      });
    } else {
      setEditingShipment(null);
      setShipmentForm({
        tracking_number: '',
        carrier: '',
        shipping_method: '',
        notes: '',
      });
    }
    setShowShipmentModal(true);
  };

  const handleShipmentSubmit = (e) => {
    e.preventDefault();
    if (!shipmentForm.tracking_number || shipmentForm.tracking_number.trim() === '') {
      notification.warning('Įveskite siuntos numerį');
      return;
    }

    if (editingShipment) {
      updateShipmentMutation.mutate({
        shipmentId: editingShipment.id,
        data: shipmentForm,
      });
    } else {
      createShipmentMutation.mutate(shipmentForm);
    }
  };

  const handleDeleteShipment = async (shipmentId) => {
    const confirmed = await notification.showConfirm(
      'Ar tikrai norite pašalinti šią siuntą?',
      'Patvirtinti siuntą pašalinimą'
    );
    if (confirmed) {
      deleteShipmentMutation.mutate(shipmentId);
    }
  };

  // RMA statuso mutacijos
  const updateRMAStatusMutation = useMutation({
    mutationFn: async ({ rmaId, data }) => {
      return apiClient.put(`/admin/rma/${rmaId}/status`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      setShowRMAStatusModal(false);
      setEditingRMA(null);
      setRmaStatusForm({ status: '', notes: '', tracking_number: '' });
      notification.success('RMA statusas atnaujintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant RMA statusą');
    },
  });

  const handleOpenRMAStatusModal = (rma) => {
    setEditingRMA(rma);
    setRmaStatusForm({
      status: rma.status,
      notes: rma.notes || '',
      tracking_number: rma.tracking_number || '',
    });
    setShowRMAStatusModal(true);
  };

  const handleRMAStatusSubmit = (e) => {
    e.preventDefault();
    if (!rmaStatusForm.status) {
      notification.warning('Pasirinkite statusą');
      return;
    }
    updateRMAStatusMutation.mutate({
      rmaId: editingRMA.id,
      data: rmaStatusForm,
    });
  };

  // Refund statuso mutacijos
  const updateRefundStatusMutation = useMutation({
    mutationFn: async ({ refundId, data }) => {
      return apiClient.put(`/admin/refunds/${refundId}/status`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-order', id]);
      setShowRefundStatusModal(false);
      setEditingRefund(null);
      setRefundStatusForm({ status: '', transaction_id: '', notes: '' });
      notification.success('Refund statusas atnaujintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant refund statusą');
    },
  });

  const handleOpenRefundStatusModal = (refund) => {
    setEditingRefund(refund);
    setRefundStatusForm({
      status: refund.status,
      transaction_id: refund.transaction_id || '',
      notes: refund.notes || '',
    });
    setShowRefundStatusModal(true);
  };

  const handleRefundStatusSubmit = (e) => {
    e.preventDefault();
    if (!refundStatusForm.status) {
      notification.warning('Pasirinkite statusą');
      return;
    }
    updateRefundStatusMutation.mutate({
      refundId: editingRefund.id,
      data: refundStatusForm,
    });
  };

  if (isLoading) {
    return <div className="loading">Kraunama...</div>;
  }

  if (!order) {
    return (
      <div className="admin-order-detail-page container">
        <div className="admin-order-not-found">
          <h2>Užsakymas nerastas</h2>
          <p>Užsakymas su nurodytu numeriu neegzistuoja arba neturite prieigos.</p>
          <button
            type="button"
            onClick={() => navigate('/admin/orders')}
            className="btn btn-primary"
          >
            ← Atgal į užsakymus
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-order-detail-page container">
      <div className="order-header">
        <div>
          <h1>Užsakymas: {order.order_number}</h1>
          <p className="order-date">Sukurta: {formatDate(order.created_at)}</p>
        </div>
        <div className="order-actions">
          <button
            onClick={() => setShowStatusModal(true)}
            className="btn btn-primary"
          >
            Keisti statusą
          </button>
          <div className="print-buttons-group">
            <button
              onClick={() => handlePrint('invoice', 'html')}
              className="btn btn-secondary"
              title="Peržiūrėti HTML"
            >
              📄 Sąskaita
            </button>
            <button
              onClick={() => handlePrint('invoice', 'pdf')}
              className="btn btn-secondary"
              title="Atsisiųsti PDF"
            >
              📥 PDF
            </button>
            <button
              onClick={() => handlePrint('packing-slip', 'html')}
              className="btn btn-secondary"
              title="Peržiūrėti HTML"
            >
              📦 Važtaraštis
            </button>
            <button
              onClick={() => handlePrint('packing-slip', 'pdf')}
              className="btn btn-secondary"
              title="Atsisiųsti PDF"
            >
              📥 PDF
            </button>
          </div>
          <button
            onClick={() => {
              const url = `/api/admin/orders/export/${id}/items/csv`;
              window.open(url, '_blank');
            }}
            className="btn btn-secondary"
          >
            📥 Eksportuoti prekes CSV
          </button>
          <button
            onClick={() => navigate('/admin/orders')}
            className="btn btn-secondary"
          >
            ← Atgal
          </button>
        </div>
      </div>

      {/* Statusų kortelės */}
      <div className="status-cards">
        <div className="status-card">
          <label>Užsakymo statusas:</label>
          <span className={`status-badge status-${order.status}`}>
            {order.status === 'pending' && 'Naujas'}
            {order.status === 'processing' && 'Ruošiamas'}
            {order.status === 'shipped' && 'Išsiųstas'}
            {order.status === 'delivered' && 'Įvykdytas'}
            {order.status === 'cancelled' && 'Atšauktas'}
          </span>
        </div>
        <div className="status-card">
          <label>Mokėjimo statusas:</label>
          <span className={`payment-badge payment-${order.payment_status}`}>
            {order.payment_status === 'pending' && 'Laukiama'}
            {order.payment_status === 'paid' && 'Apmokėta'}
            {order.payment_status === 'failed' && 'Nepavyko'}
            {order.payment_status === 'refunded' && 'Grąžinta'}
            {order.payment_status === 'chargeback' && 'Chargeback'}
            {order.payment_status === 'partially_refunded' && 'Dalinis grąžinimas'}
          </span>
          <button
            onClick={() => {
              setPaymentStatusForm({
                payment_status: order.payment_status || '',
                amount: order.total || '',
                transaction_id: '',
                payment_method: order.payment_method || '',
                notes: ''
              });
              setShowPaymentStatusModal(true);
            }}
            className="btn btn-sm btn-secondary"
            style={{ marginTop: '8px' }}
          >
            Keisti mokėjimo būseną
          </button>
        </div>
        <div className="status-card">
          <label>Mokėjimo būdas:</label>
          <span>{order.payment_method || '—'}</span>
        </div>
        {order.tracking_number && (
          <div className="status-card">
            <label>Siuntos numeris:</label>
            <span>{order.tracking_number}</span>
          </div>
        )}
      </div>

      <div className="order-content-grid">
        {/* Kairė pusė */}
        <div className="order-main">
          {/* Kliento informacija */}
          <div className="info-section">
            <h2>Kliento informacija</h2>
            <div className="info-grid">
              <div>
                <label>Vardas, pavardė:</label>
                <p>
                  {order.customer_first_name && order.customer_last_name
                    ? `${order.customer_first_name} ${order.customer_last_name}`
                    : 'Svečias'}
                </p>
              </div>
              <div>
                <label>El. paštas:</label>
                <p>{order.customer_email || '—'}</p>
              </div>
              <div>
                <label>Telefonas:</label>
                <p>{order.customer_phone || '—'}</p>
              </div>
            </div>
          </div>

          {/* Atsiskaitymo adresas */}
          <div className="info-section">
            <h2>Atsiskaitymo adresas</h2>
            <div className="address-box">
              {order.billing_address ? (
                <>
                  <p><strong>{order.billing_address.first_name} {order.billing_address.last_name}</strong></p>
                  {order.billing_address.company && <p>{order.billing_address.company}</p>}
                  <p>{order.billing_address.address_line1}</p>
                  {order.billing_address.address_line2 && <p>{order.billing_address.address_line2}</p>}
                  <p>{order.billing_address.city}, {order.billing_address.postal_code}</p>
                  <p>{order.billing_address.country}</p>
                  {order.billing_address.phone && <p>Tel: {order.billing_address.phone}</p>}
                </>
              ) : (
                <p>—</p>
              )}
            </div>
          </div>

          {/* Pristatymo adresas */}
          <div className="info-section">
            <h2>Pristatymo adresas</h2>
            <div className="address-box">
              {order.shipping_address ? (
                <>
                  <p><strong>{order.shipping_address.first_name} {order.shipping_address.last_name}</strong></p>
                  {order.shipping_address.company && <p>{order.shipping_address.company}</p>}
                  <p>{order.shipping_address.address_line1}</p>
                  {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                  <p>{order.shipping_address.city}, {order.shipping_address.postal_code}</p>
                  <p>{order.shipping_address.country}</p>
                  {order.shipping_address.phone && <p>Tel: {order.shipping_address.phone}</p>}
                </>
              ) : (
                <p>—</p>
              )}
            </div>
          </div>

          {/* Prekės */}
          <div className="info-section">
            <h2>Prekės</h2>
            <table className="order-items-table">
              <thead>
                <tr>
                  <th>Prekė</th>
                  <th>SKU</th>
                  <th>Kiekis</th>
                  <th>Kaina</th>
                  <th>Suma</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div>
                        <strong>{item.product_name}</strong>
                        {item.variant_name && (
                          <div className="variant-info">Variantas: {item.variant_name}</div>
                        )}
                      </div>
                    </td>
                    <td>{item.product_sku || '—'}</td>
                    <td>{item.quantity}</td>
                    <td>{parseFloat(item.price).toFixed(2)} €</td>
                    <td><strong>{parseFloat(item.total).toFixed(2)} €</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Statusų istorija */}
          {order.statusHistory && order.statusHistory.length > 0 && (
            <div className="info-section">
              <h2>Statusų istorija</h2>
              <div className="status-history">
                {order.statusHistory.map((history) => (
                  <div key={history.id} className="history-item">
                    <div className="history-header">
                      <span className="history-status">
                        {history.old_status} → {history.new_status}
                      </span>
                      <span className="history-date">{formatDate(history.created_at)}</span>
                    </div>
                    {history.changed_by_email && (
                      <div className="history-user">Pakeitė: {history.changed_by_email}</div>
                    )}
                    {history.notes && (
                      <div className="history-notes">{history.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dešinė pusė */}
        <div className="order-sidebar">
          {/* Sumos */}
          <div className="summary-box">
            <h3>Sumos</h3>
            <div className="summary-row">
              <span>Prekių suma:</span>
              <span>{parseFloat(order.subtotal).toFixed(2)} €</span>
            </div>
            <div className="summary-row">
              <span>PVM (21%):</span>
              <span>{parseFloat(order.tax).toFixed(2)} €</span>
            </div>
            <div className="summary-row">
              <span>Pristatymas:</span>
              <span>{parseFloat(order.shipping_cost).toFixed(2)} €</span>
            </div>
            <div className="summary-row total">
              <span>Iš viso:</span>
              <span>{parseFloat(order.total).toFixed(2)} €</span>
            </div>
          </div>

          {/* Pastabos */}
          {order.notes && (
            <div className="notes-box">
              <h3>Pastabos</h3>
              <p>{order.notes}</p>
            </div>
          )}

          {/* Sąskaitos faktūros */}
          <div className="invoices-box">
            <div className="box-header">
              <h3>Sąskaitos faktūros</h3>
              <button
                onClick={() => navigate(`/admin/invoices?create_order_id=${id}`)}
                className="btn btn-sm btn-primary"
              >
                + Sukurti sąskaitą faktūrą
              </button>
            </div>
            {invoicesData?.invoices && invoicesData.invoices.length > 0 ? (
              <div className="invoices-table-wrapper">
                <table className="invoices-table">
                  <thead>
                    <tr>
                      <th>Sąskaitos Nr.</th>
                      <th>Klientas</th>
                      <th>Data</th>
                      <th>Terminas</th>
                      <th>Suma</th>
                      <th>Statusas</th>
                      <th>Veiksmai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesData.invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <Link to={`/admin/invoices/${invoice.id}`} className="invoice-link">
                            {invoice.invoice_number}
                          </Link>
                        </td>
                        <td>
                          {invoice.user_email || (
                            <span className="guest-customer">Svečias</span>
                          )}
                          {invoice.first_name && (
                            <div className="customer-name">
                              {invoice.first_name} {invoice.last_name}
                            </div>
                          )}
                        </td>
                        <td>{formatDateShort(invoice.issue_date)}</td>
                        <td>{formatDateShort(invoice.due_date)}</td>
                        <td className="amount">{parseFloat(invoice.total || 0).toFixed(2)} €</td>
                        <td>
                          <span className={`status-badge ${getInvoiceStatusBadgeClass(invoice.status)}`}>
                            {getInvoiceStatusLabel(invoice.status)}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/admin/invoices/${invoice.id}`}
                            className="btn btn-sm btn-primary"
                          >
                            Peržiūrėti
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-message">Sąskaitų faktūrų nėra</p>
            )}
          </div>

          {/* Pick & Pack: Surinkimo lapai */}
          {pickPackData?.pick_lists && pickPackData.pick_lists.length > 0 && (
            <div className="pick-pack-box">
              <div className="box-header">
                <h3>Surinkimo lapai</h3>
                <button
                  onClick={() => createPickListMutation.mutate()}
                  className="btn btn-sm btn-primary"
                  disabled={createPickListMutation.isLoading}
                >
                  + Sukurti surinkimo lapą
                </button>
              </div>
              {pickPackData.pick_lists.map((pickList) => (
                <div key={pickList.id} className="pick-list-item">
                  <div className="pick-list-header">
                    <div>
                      <div><strong>Statusas:</strong>{' '}
                        <span className={`status-badge status-${pickList.status}`}>
                          {pickList.status === 'pending' && 'Laukiama'}
                          {pickList.status === 'in_progress' && 'Vykdoma'}
                          {pickList.status === 'completed' && 'Užbaigta'}
                          {pickList.status === 'cancelled' && 'Atšaukta'}
                        </span>
                      </div>
                      {pickList.assigned_to_email && (
                        <div><strong>Priskirta:</strong> {pickList.assigned_to_email}</div>
                      )}
                      {pickList.started_at && (
                        <div><strong>Pradėta:</strong> {formatDate(pickList.started_at)}</div>
                      )}
                      {pickList.completed_at && (
                        <div><strong>Užbaigta:</strong> {formatDate(pickList.completed_at)}</div>
                      )}
                    </div>
                  </div>
                  {pickList.items && pickList.items.length > 0 && (
                    <div className="pick-list-items">
                      <table className="pick-items-table">
                        <thead>
                          <tr>
                            <th>Prekė</th>
                            <th>Reikia</th>
                            <th>Surinkta</th>
                            <th>Vieta</th>
                            <th>Statusas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pickList.items.map((item) => (
                            <tr key={item.id}>
                              <td>{item.product_name || item.product_name_full}</td>
                              <td>{item.quantity_required}</td>
                              <td>{item.quantity_picked || 0}</td>
                              <td>{item.location || '—'}</td>
                              <td>
                                <span className={`status-badge status-${item.status}`}>
                                  {item.status === 'pending' && 'Laukiama'}
                                  {item.status === 'picking' && 'Renkama'}
                                  {item.status === 'picked' && 'Surinkta'}
                                  {item.status === 'missing' && 'Trūksta'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Siuntos */}
          <div className="shipments-box">
            <div className="box-header">
              <h3>Siuntos</h3>
              <button
                onClick={() => handleOpenShipmentModal()}
                className="btn btn-sm btn-primary"
              >
                + Pridėti siuntą
              </button>
            </div>
            {order.shipments && order.shipments.length > 0 ? (
              order.shipments.map((shipment) => (
                <div key={shipment.id} className="shipment-item">
                  <div className="shipment-header">
                    <div>
                      <div><strong>Tracking:</strong> {shipment.tracking_number}</div>
                      <div><strong>Vežėjas:</strong> {shipment.carrier_display_name || shipment.carrier || '—'}</div>
                      {shipment.shipping_method && (
                        <div><strong>Metodas:</strong> {shipment.shipping_method}</div>
                      )}
                      <div><strong>Statusas:</strong> 
                        <span className={`status-badge status-${shipment.status}`}>
                          {shipment.status === 'pending' && 'Laukiama'}
                          {shipment.status === 'in_transit' && 'Kelyje'}
                          {shipment.status === 'delivered' && 'Pristatyta'}
                          {shipment.status === 'returned' && 'Grąžinta'}
                          {shipment.status === 'lost' && 'Prastota'}
                        </span>
                      </div>
                      {shipment.shipped_at && (
                        <div><strong>Išsiųsta:</strong> {formatDate(shipment.shipped_at)}</div>
                      )}
                      {shipment.delivered_at && (
                        <div><strong>Pristatyta:</strong> {formatDate(shipment.delivered_at)}</div>
                      )}
                      {shipment.notes && (
                        <div><strong>Pastabos:</strong> {shipment.notes}</div>
                      )}
                    </div>
                    <div className="shipment-actions">
                      <button
                        onClick={() => handleOpenShipmentModal(shipment)}
                        className="btn btn-sm btn-secondary"
                      >
                        Redaguoti
                      </button>
                      <button
                        onClick={() => handleDeleteShipment(shipment.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Pašalinti
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-message">Siuntų nėra</p>
            )}
          </div>


          {/* Refundai */}
          <div className="refunds-box">
            <div className="box-header">
              <h3>Refundai</h3>
              <button
                onClick={() => navigate(`/admin/orders/${id}/refund`)}
                className="btn btn-sm btn-primary"
              >
                + Pridėti refund
              </button>
            </div>
            {order.refunds && order.refunds.length > 0 ? (
              order.refunds.map((refund) => (
                <div key={refund.id} className="refund-item">
                  <div className="refund-header">
                    <div>
                      <div><strong>Nr:</strong> {refund.refund_number}</div>
                      <div><strong>Suma:</strong> {parseFloat(refund.amount).toFixed(2)} €</div>
                      <div><strong>Statusas:</strong> 
                        <span className={`status-badge status-${refund.status}`}>
                          {refund.status === 'pending' && 'Laukiama'}
                          {refund.status === 'processing' && 'Apdorojama'}
                          {refund.status === 'completed' && 'Užbaigta'}
                          {refund.status === 'failed' && 'Nepavyko'}
                          {refund.status === 'cancelled' && 'Atšaukta'}
                        </span>
                      </div>
                      <div><strong>Būdas:</strong> {refund.refund_method}</div>
                      {refund.transaction_id && (
                        <div><strong>Transakcija:</strong> {refund.transaction_id}</div>
                      )}
                      {refund.processed_at && (
                        <div><strong>Apdorota:</strong> {formatDate(refund.processed_at)}</div>
                      )}
                    </div>
                    <div className="refund-actions">
                      <button
                        onClick={() => handleOpenRefundStatusModal(refund)}
                        className="btn btn-sm btn-secondary"
                      >
                        Keisti statusą
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-message">Refundų nėra</p>
            )}
          </div>

          {/* Mokėjimų būsenų istorija */}
          {paymentHistory && paymentHistory.length > 0 && (
            <div className="payment-history-box">
              <div className="box-header">
                <h3>Mokėjimų būsenų istorija</h3>
              </div>
              <div className="history-list">
                {paymentHistory.map((entry) => (
                  <div key={entry.id} className="history-item">
                    <div className="history-content">
                      <div className="history-status">
                        <span className={`status-badge status-${entry.payment_status}`}>
                          {entry.payment_status === 'pending' && 'Laukiama'}
                          {entry.payment_status === 'paid' && 'Apmokėta'}
                          {entry.payment_status === 'failed' && 'Nepavyko'}
                          {entry.payment_status === 'refunded' && 'Grąžinta'}
                          {entry.payment_status === 'chargeback' && 'Chargeback'}
                          {entry.payment_status === 'partially_refunded' && 'Dalinis grąžinimas'}
                        </span>
                      </div>
                      <div className="history-details">
                        {entry.amount && (
                          <div><strong>Suma:</strong> {parseFloat(entry.amount).toFixed(2)} €</div>
                        )}
                        {entry.transaction_id && (
                          <div><strong>Transakcija:</strong> {entry.transaction_id}</div>
                        )}
                        {entry.payment_method && (
                          <div><strong>Būdas:</strong> {entry.payment_method}</div>
                        )}
                        {entry.notes && (
                          <div><strong>Pastabos:</strong> {entry.notes}</div>
                        )}
                        {entry.created_by_email && (
                          <div><strong>Pakeitė:</strong> {entry.created_by_email}</div>
                        )}
                        <div><strong>Data:</strong> {formatDate(entry.created_at)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grąžinimai (RMA) */}
          <div className="returns-box">
            <div className="box-header">
              <h3>Grąžinimai (RMA)</h3>
              <button
                onClick={() => navigate(`/admin/orders/${id}/return`)}
                className="btn btn-sm btn-primary"
              >
                + Pridėti RMA
              </button>
            </div>
            {order.returns && order.returns.length > 0 ? (
              order.returns.map((returnItem) => (
                <div key={returnItem.id} className="return-item">
                  <div className="return-header">
                    <div>
                      <div><strong>Nr:</strong> {returnItem.return_number}</div>
                      <div><strong>Statusas:</strong> 
                        <span className={`status-badge status-${returnItem.status}`}>
                          {returnItem.status === 'requested' && 'Prašoma'}
                          {returnItem.status === 'approved' && 'Patvirtinta'}
                          {returnItem.status === 'rejected' && 'Atmesta'}
                          {returnItem.status === 'received' && 'Gauta'}
                          {returnItem.status === 'refunded' && 'Grąžinta'}
                          {returnItem.status === 'cancelled' && 'Atšaukta'}
                        </span>
                      </div>
                      <div><strong>Suma:</strong> {parseFloat(returnItem.refund_amount).toFixed(2)} €</div>
                      {returnItem.tracking_number && (
                        <div><strong>Tracking:</strong> {returnItem.tracking_number}</div>
                      )}
                      {returnItem.reason && (
                        <div><strong>Priežastis:</strong> {returnItem.reason}</div>
                      )}
                      {returnItem.processed_at && (
                        <div><strong>Apdorota:</strong> {formatDate(returnItem.processed_at)}</div>
                      )}
                    </div>
                    <div className="return-actions">
                      <button
                        onClick={() => handleOpenRMAStatusModal(returnItem)}
                        className="btn btn-sm btn-secondary"
                      >
                        Keisti statusą
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-message">Grąžinimų nėra</p>
            )}
          </div>
        </div>
      </div>

      {/* Statusų keitimo modalas */}
      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Keisti užsakymo statusą</h2>
            
            <div className="form-group">
              <label>Užsakymo statusas:</label>
              <select
                value={statusForm.status}
                onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                className="input"
              >
                <option value="">Nekeisti</option>
                {getStatusOptions(order.status).map((status) => (
                  <option key={status} value={status}>
                    {status === 'pending' && 'Naujas'}
                    {status === 'processing' && 'Ruošiamas'}
                    {status === 'shipped' && 'Išsiųstas'}
                    {status === 'delivered' && 'Įvykdytas'}
                    {status === 'cancelled' && 'Atšauktas'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Mokėjimo statusas:</label>
              <select
                value={statusForm.payment_status}
                onChange={(e) => setStatusForm({ ...statusForm, payment_status: e.target.value })}
                className="input"
              >
                <option value="">Nekeisti</option>
                {getPaymentStatusOptions(order.payment_status).map((status) => (
                  <option key={status} value={status}>
                    {status === 'pending' && 'Laukiama'}
                    {status === 'paid' && 'Apmokėta'}
                    {status === 'failed' && 'Nepavyko'}
                    {status === 'refunded' && 'Grąžinta'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Pastabos:</label>
              <textarea
                value={statusForm.notes}
                onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                className="input"
                rows="3"
                placeholder="Pastabos apie statuso keitimą..."
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
              >
                Atšaukti
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={updateStatusMutation.isLoading}
                className="btn btn-primary"
              >
                {updateStatusMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Siuntų valdymo modalas */}
      {showShipmentModal && (
        <div className="modal-overlay" onClick={() => setShowShipmentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingShipment ? 'Redaguoti siuntą' : 'Pridėti siuntą'}</h2>
            
            <form onSubmit={handleShipmentSubmit}>
              <div className="form-group">
                <label>Tracking number *</label>
                <input
                  type="text"
                  value={shipmentForm.tracking_number}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, tracking_number: e.target.value })}
                  className="input"
                  required
                  placeholder="LT123456789"
                />
              </div>

              <div className="form-group">
                <label>Vežėjas</label>
                <select
                  value={shipmentForm.carrier}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, carrier: e.target.value })}
                  className="input"
                >
                  <option value="">Pasirinkite vežėją</option>
                  {(() => {
                    const fromMethods = (shippingMethods || [])
                      .map((m) => (m.carrier_name || '').trim())
                      .filter(Boolean);
                    const uniqueCarriers = [...new Set(fromMethods)];
                    return (
                      <>
                        {uniqueCarriers.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                        {editingShipment && shipmentForm.carrier && !uniqueCarriers.includes(shipmentForm.carrier) && (
                          <option value={shipmentForm.carrier}>{shipmentForm.carrier}</option>
                        )}
                      </>
                    );
                  })()}
                </select>
                {(!shippingMethods?.length || (shippingMethods || []).every((m) => !(m.carrier_name || '').trim())) && (
                  <p className="form-hint" style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
                    Vežėjų sąrašas imamas iš pristatymo metodų ir taisyklių. Nustatykite lauką „Vežėjas“ pristatymo metoduose: Administravimas → Pristatymo metodai.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Pristatymo būdas</label>
                <select
                  value={shipmentForm.shipping_method}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, shipping_method: e.target.value })}
                  className="input"
                >
                  <option value="">Pasirinkite pristatymo būdą</option>
                  {shippingMethods.map((method) => (
                    <option key={method.id} value={method.name}>
                      {method.name}
                    </option>
                  ))}
                  {editingShipment && shipmentForm.shipping_method && !shippingMethods.some(
                    (m) => m.name === shipmentForm.shipping_method
                  ) && (
                    <option value={shipmentForm.shipping_method}>{shipmentForm.shipping_method}</option>
                  )}
                </select>
                {shippingMethods.length === 0 && (
                  <p className="form-hint" style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
                    Nėra pristatymo metodų. Pridėkite juos: Administravimas → Pristatymo metodai.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Pastabos</label>
                <textarea
                  value={shipmentForm.notes}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Papildomos pastabos..."
                />
              </div>

              <div className="info-box" style={{ marginBottom: '16px', padding: '12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#0369a1' }}>
                  ℹ️ Vežėjas ir pristatymo būdas pasirenkami iš sistemoje sukonfigūruotų. Jei nepasirinksite, jie gali būti automatiškai paimti iš užsakymo (jei nustatyta). Užsakymo statusas automatiškai pasikeis į "Išsiųstas".
                </p>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowShipmentModal(false);
                    setEditingShipment(null);
                    setShipmentForm({
                      tracking_number: '',
                      carrier: '',
                      shipping_method: '',
                      notes: '',
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={createShipmentMutation.isLoading || updateShipmentMutation.isLoading}
                  className="btn btn-primary"
                >
                  {createShipmentMutation.isLoading || updateShipmentMutation.isLoading
                    ? 'Išsaugoma...'
                    : editingShipment
                    ? 'Atnaujinti'
                    : 'Sukurti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RMA statuso keitimo modalas */}
      {showRMAStatusModal && editingRMA && (
        <div className="modal-overlay" onClick={() => setShowRMAStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Keisti RMA statusą</h2>
            <p className="modal-subtitle">RMA: {editingRMA.return_number}</p>
            
            <form onSubmit={handleRMAStatusSubmit}>
              <div className="form-group">
                <label>Statusas *</label>
                <select
                  value={rmaStatusForm.status}
                  onChange={(e) => setRmaStatusForm({ ...rmaStatusForm, status: e.target.value })}
                  className="input"
                  required
                >
                  <option value="requested">Prašoma</option>
                  <option value="approved">Patvirtinta</option>
                  <option value="rejected">Atmesta</option>
                  <option value="received">Gauta</option>
                  <option value="refunded">Grąžinta</option>
                  <option value="cancelled">Atšaukta</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tracking number</label>
                <input
                  type="text"
                  value={rmaStatusForm.tracking_number}
                  onChange={(e) => setRmaStatusForm({ ...rmaStatusForm, tracking_number: e.target.value })}
                  className="input"
                  placeholder="Įveskite tracking number..."
                />
              </div>

              <div className="form-group">
                <label>Pastabos</label>
                <textarea
                  value={rmaStatusForm.notes}
                  onChange={(e) => setRmaStatusForm({ ...rmaStatusForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Pastabos apie statuso keitimą..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowRMAStatusModal(false);
                    setEditingRMA(null);
                    setRmaStatusForm({ status: '', notes: '', tracking_number: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={updateRMAStatusMutation.isLoading}
                  className="btn btn-primary"
                >
                  {updateRMAStatusMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mokėjimo būsenos keitimo modalas */}
      {showPaymentStatusModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Keisti mokėjimo būseną</h2>
            <p className="modal-subtitle">Užsakymas: {order?.order_number}</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              updatePaymentStatusMutation.mutate(paymentStatusForm);
            }}>
              <div className="form-group">
                <label>Mokėjimo statusas *</label>
                <select
                  value={paymentStatusForm.payment_status}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, payment_status: e.target.value })}
                  className="input"
                  required
                >
                  <option value="pending">Laukiama</option>
                  <option value="paid">Apmokėta</option>
                  <option value="failed">Nepavyko</option>
                  <option value="refunded">Grąžinta</option>
                  <option value="chargeback">Chargeback</option>
                  <option value="partially_refunded">Dalinis grąžinimas</option>
                </select>
              </div>

              <div className="form-group">
                <label>Suma</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentStatusForm.amount}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, amount: e.target.value })}
                  className="input"
                  placeholder="Mokėjimo suma"
                />
              </div>

              <div className="form-group">
                <label>Transakcijos ID</label>
                <input
                  type="text"
                  value={paymentStatusForm.transaction_id}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, transaction_id: e.target.value })}
                  className="input"
                  placeholder="Transakcijos numeris"
                />
              </div>

              <div className="form-group">
                <label>Mokėjimo būdas</label>
                <input
                  type="text"
                  value={paymentStatusForm.payment_method}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, payment_method: e.target.value })}
                  className="input"
                  placeholder="Stripe, PayPal, Banklink..."
                />
              </div>

              <div className="form-group">
                <label>Pastabos</label>
                <textarea
                  value={paymentStatusForm.notes}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Pastabos apie mokėjimo būsenos keitimą..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentStatusModal(false);
                    setPaymentStatusForm({ 
                      payment_status: '', 
                      amount: '', 
                      transaction_id: '', 
                      payment_method: '', 
                      notes: '' 
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={updatePaymentStatusMutation.isLoading}
                  className="btn btn-primary"
                >
                  {updatePaymentStatusMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mokėjimo būsenos keitimo modalas */}
      {showPaymentStatusModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Keisti mokėjimo būseną</h2>
            <p className="modal-subtitle">Užsakymas: {order?.order_number}</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              updatePaymentStatusMutation.mutate(paymentStatusForm);
            }}>
              <div className="form-group">
                <label>Mokėjimo statusas *</label>
                <select
                  value={paymentStatusForm.payment_status}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, payment_status: e.target.value })}
                  className="input"
                  required
                >
                  <option value="pending">Laukiama</option>
                  <option value="paid">Apmokėta</option>
                  <option value="failed">Nepavyko</option>
                  <option value="refunded">Grąžinta</option>
                  <option value="chargeback">Chargeback</option>
                  <option value="partially_refunded">Dalinis grąžinimas</option>
                </select>
              </div>

              <div className="form-group">
                <label>Suma</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentStatusForm.amount}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, amount: e.target.value })}
                  className="input"
                  placeholder="Mokėjimo suma"
                />
              </div>

              <div className="form-group">
                <label>Transakcijos ID</label>
                <input
                  type="text"
                  value={paymentStatusForm.transaction_id}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, transaction_id: e.target.value })}
                  className="input"
                  placeholder="Transakcijos numeris"
                />
              </div>

              <div className="form-group">
                <label>Mokėjimo būdas</label>
                <input
                  type="text"
                  value={paymentStatusForm.payment_method}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, payment_method: e.target.value })}
                  className="input"
                  placeholder="Stripe, PayPal, Banklink..."
                />
              </div>

              <div className="form-group">
                <label>Pastabos</label>
                <textarea
                  value={paymentStatusForm.notes}
                  onChange={(e) => setPaymentStatusForm({ ...paymentStatusForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Pastabos apie mokėjimo būsenos keitimą..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentStatusModal(false);
                    setPaymentStatusForm({ 
                      payment_status: '', 
                      amount: '', 
                      transaction_id: '', 
                      payment_method: '', 
                      notes: '' 
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={updatePaymentStatusMutation.isLoading}
                  className="btn btn-primary"
                >
                  {updatePaymentStatusMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund statuso keitimo modalas */}
      {showRefundStatusModal && editingRefund && (
        <div className="modal-overlay" onClick={() => setShowRefundStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Keisti refund statusą</h2>
            <p className="modal-subtitle">Refund: {editingRefund.refund_number}</p>
            
            <form onSubmit={handleRefundStatusSubmit}>
              <div className="form-group">
                <label>Statusas *</label>
                <select
                  value={refundStatusForm.status}
                  onChange={(e) => setRefundStatusForm({ ...refundStatusForm, status: e.target.value })}
                  className="input"
                  required
                >
                  <option value="pending">Laukiama</option>
                  <option value="processing">Apdorojama</option>
                  <option value="completed">Užbaigta</option>
                  <option value="failed">Nepavyko</option>
                  <option value="cancelled">Atšaukta</option>
                </select>
              </div>

              <div className="form-group">
                <label>Transakcijos ID</label>
                <input
                  type="text"
                  value={refundStatusForm.transaction_id}
                  onChange={(e) => setRefundStatusForm({ ...refundStatusForm, transaction_id: e.target.value })}
                  className="input"
                  placeholder="Įveskite transakcijos ID..."
                />
              </div>

              <div className="form-group">
                <label>Pastabos</label>
                <textarea
                  value={refundStatusForm.notes}
                  onChange={(e) => setRefundStatusForm({ ...refundStatusForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Pastabos apie statuso keitimą..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowRefundStatusModal(false);
                    setEditingRefund(null);
                    setRefundStatusForm({ status: '', transaction_id: '', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={updateRefundStatusMutation.isLoading}
                  className="btn btn-primary"
                >
                  {updateRefundStatusMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrderDetail;
